import { useCallback } from 'react';
import { useFacebookSdk } from './useFacebookSdk';
import useApi from '../../api/backendApiContext';
import { useMutation } from '@tanstack/react-query';
import { analyticsTrack } from '../../web-analytics/webAnalytics';
import { AnalyticsEvent } from '../../web-analytics/AnalyticsEvent';
import { errorToString } from '../../util/utils';
import { useNotification } from '../../notification/notificationsContext';
import { useQueryCacheManager } from '../../hooks/useQueryCacheManager';

const requiredFacebookScopes = [
  'business_management',
  'pages_show_list',
  'pages_read_engagement',
  'pages_manage_posts',
  'instagram_basic',
  'instagram_content_publish',
];

export const useConnectFacebook = () => {
  const { sdkLoaded } = useFacebookSdk();
  const { socialPostingApi } = useApi();
  const { notifySuccess, notifyError } = useNotification();
  const queryCacheManager = useQueryCacheManager();
  const { isLoading: isConnecting, mutate: connect } = useMutation(
    async (accessToken: string) => {
      return socialPostingApi.authenticateFacebook(accessToken);
    },
    {
      onSuccess: (facebookAccount) => {
        queryCacheManager.setFacebookAccount(facebookAccount);
        const message = 'Facebook account has been successfully connected.';
        notifySuccess({
          notificationMsg: message,
          logMsg: message,
        });
        analyticsTrack(AnalyticsEvent.FACEBOOK_CONNECTED);
      },
      onError: (e) => {
        analyticsTrack(AnalyticsEvent.FACEBOOK_CONNECTION_FAILED);
        const message = errorToString(
          e,
          'Something when wrong while setting up Facebook connection. Please try again.'
        );
        notifyError({
          notificationMsg: message,
          logMsg: message,
        });
      },
    }
  );

  const startConnect = useCallback(() => {
    try {
      if (!sdkLoaded) {
        logger.warn('Facebook SDK not loaded, cannot initiate login.');
        return;
      }
      window.FB.login(
        (response: fb.StatusResponse) => {
          if (response.authResponse?.accessToken) connect(response.authResponse.accessToken);
          else
            notifyError({
              notificationMsg:
                'Connection with Facebook and Instagram was not complete, please try again.',
            });
        },
        {
          scope: requiredFacebookScopes.join(','),
          auth_type: 'reauthenticate',
          return_scopes: true,
          enable_profile_selector: true,
          response_type: 'token',
        }
      );
      logger.info(`Initiating Facebook login.`);
    } catch (error) {
      const notificationMsg =
        'Connection with Facebook and Instagram was not complete, please try again.';
      notifyError({
        err: error,
        notificationMsg: notificationMsg,
      });
    }
  }, [sdkLoaded, notifyError, connect]);

  return { startConnect, isConnecting };
};
